
import { useState } from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Clock, AlertCircle, Trash2 } from "lucide-react";
import { Client } from "@/types/database";
import { ConfirmDeleteDialog } from "@/pages/approvals/components/ConfirmDeleteDialog";

interface Transaction {
  id: string;
  description: string;
  amount: number;
  category: string;
  date: Date;
  type: "income" | "expense";
  status: "paid" | "pending" | "overdue";
  clientName?: string;
  client_id?: string | null;
}

interface TransactionListProps {
  transactions: Transaction[];
  clients: Client[];
  onStatusChange: (id: string, newStatus: "paid" | "pending" | "overdue") => void;
  onDelete?: (id: string) => void;
}

export function TransactionList({ transactions, clients, onStatusChange, onDelete }: TransactionListProps) {
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedTransactionId, setSelectedTransactionId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteClick = (id: string) => {
    setSelectedTransactionId(id);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (selectedTransactionId && onDelete) {
      setIsDeleting(true);
      await onDelete(selectedTransactionId);
      setIsDeleting(false);
      setDeleteConfirmOpen(false);
      setSelectedTransactionId(null);
    }
  };

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Descrição</TableHead>
            <TableHead>Tipo</TableHead>
            <TableHead>Valor</TableHead>
            <TableHead>Categoria</TableHead>
            <TableHead>Data</TableHead>
            <TableHead>Cliente</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {transactions.length > 0 ? (
            transactions.map((transaction) => (
              <TableRow key={transaction.id}>
                <TableCell className="font-medium">{transaction.description}</TableCell>
                <TableCell>
                  <Badge variant={transaction.type === "income" ? "default" : "destructive"}>
                    {transaction.type === "income" ? "Receita" : "Despesa"}
                  </Badge>
                </TableCell>
                <TableCell
                  className={`font-medium ${
                    transaction.type === "income" ? "text-green-600" : "text-red-600"
                  }`}
                >
                  R$ {transaction.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </TableCell>
                <TableCell>{transaction.category}</TableCell>
                <TableCell>
                  {format(transaction.date, "dd 'de' MMMM", { locale: ptBR })}
                </TableCell>
                <TableCell>{transaction.clientName || "-"}</TableCell>
                <TableCell>
                  {transaction.status === "paid" ? (
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                      <CheckCircle2 className="mr-1 h-3 w-3" /> Pago
                    </Badge>
                  ) : transaction.status === "pending" ? (
                    <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                      <Clock className="mr-1 h-3 w-3" /> Pendente
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                      <AlertCircle className="mr-1 h-3 w-3" /> Atrasado
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex space-x-2">
                    {transaction.status !== "paid" && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 border-green-200 text-green-700 hover:bg-green-50"
                        onClick={() => onStatusChange(transaction.id, "paid")}
                      >
                        <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                        Pago
                      </Button>
                    )}
                    {transaction.status !== "pending" && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8"
                        onClick={() => onStatusChange(transaction.id, "pending")}
                      >
                        <Clock className="h-3.5 w-3.5 mr-1" />
                        Pendente
                      </Button>
                    )}
                    {transaction.status !== "overdue" && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 border-red-200 text-red-700 hover:bg-red-50"
                        onClick={() => onStatusChange(transaction.id, "overdue")}
                      >
                        <AlertCircle className="h-3.5 w-3.5 mr-1" />
                        Atrasado
                      </Button>
                    )}
                    {onDelete && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 border-red-200 text-red-700 hover:bg-red-50"
                        onClick={() => handleDeleteClick(transaction.id)}
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-1" />
                        Excluir
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={8} className="text-center py-4 text-muted-foreground">
                Nenhuma transação encontrada para os filtros atuais.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      
      <ConfirmDeleteDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title="Excluir Transação"
        description="Tem certeza que deseja excluir esta transação? Esta ação não pode ser desfeita."
        onConfirm={handleConfirmDelete}
        loading={isDeleting}
        confirmText="Excluir"
        cancelText="Cancelar"
      />
    </div>
  );
}
